import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import ExchangeProgressBar from './ExchangeProgressBar';
import {
  ImbursementStatusType,
  devexHelpPageUrl,
  getCashoutUrl,
  getMyMoneyUrl,
  getMembershipUrl
} from '../constants/devexConstants';
import getDeveloperExchangeInfo from '../services/devexService';

function DevExWidget({ translate, intl }) {
  const [devexInfo, setDevexInfo] = useState(null);

  useEffect(() => {
    getDeveloperExchangeInfo().then(result => {
      setDevexInfo(result.data);
    });
  }, []);

  const localeDateString = dateStr => {
    const date = new Date(dateStr);
    if (dateStr && date) {
      return intl.d(date, 'short');
    }
    return dateStr;
  };

  function showTitleMessage() {
    let lastImbursementMessage = null;
    const lineAdjustment = '<br>';
    switch (devexInfo.lastImbursementStatus) {
      case ImbursementStatusType.Pending:
        lastImbursementMessage =
          translate('Description.DevExRequestSubmittedOn', {
            requestDate: localeDateString(devexInfo.lastImbursementSubmissionDate)
          }) +
          lineAdjustment +
          translate('Label.DevExStatusPending');
        break;
      case ImbursementStatusType.Completed:
        lastImbursementMessage = translate('Description.DevExRequestCompleted', {
          startMoneyLink: `<a class="text-link" href='${getMyMoneyUrl()}' target='_blank'>`,
          endMoneyLink: '</a>'
        });
        break;
      case ImbursementStatusType.Rejected:
        lastImbursementMessage =
          translate('Description.DevExRequestSubmittedOn', {
            requestDate: localeDateString(devexInfo.lastImbursementSubmissionDate)
          }) +
          lineAdjustment +
          translate('Label.DevExStatusRejected');
        break;
      default:
        lastImbursementMessage = null;
    }

    if (lastImbursementMessage) {
      return (
        <Fragment>
          <h2>{translate('Heading.DeveloperExchange')}</h2>
          <h3 className='space-above'>{translate('Heading.YourUpdate')}</h3>
          <div dangerouslySetInnerHTML={{ __html: lastImbursementMessage }} />
        </Fragment>
      );
    }

    return <h2>{translate('Heading.DeveloperExchange')}</h2>;
  }

  function showSubtitle() {
    if (devexInfo.showOnlyExchangeRates) {
      // return here is we don't need to display anything
      return <Fragment />;
    }
    let subtitleStr = null;
    let helpTipStr = null;
    if (devexInfo.isImbursementBlacklistUser) {
      subtitleStr = translate('Label.NotEligible');
    } else if (!devexInfo.emailIsVerified) {
      subtitleStr = translate('Label.AlmostReady');
      helpTipStr = translate('Label.NeedVerifiedEmail');
    } else if (devexInfo.showProgressBar) {
      subtitleStr =
        devexInfo.percentRobux < 75
          ? translate('Label.TradingRobux')
          : translate('Label.TradingRobuxCash');
    }
    return (
      subtitleStr && (
        <Fragment>
          <div className='subtitle'>{subtitleStr}</div>
          <div className='space-above'>{helpTipStr}</div>
        </Fragment>
      )
    );
  }

  function showProgressBar() {
    const ifShowProgressBar = devexInfo.showProgressBar;
    const barInfo = {
      percentRobux: devexInfo.percentRobux,
      targetRobux: devexInfo.minRobuxToCashOut,
      currencyOperationErrorMessage:
        devexInfo.hasCurrencyOperationError && devexInfo.currencyOperationErrorMessage
    };
    return ifShowProgressBar && <ExchangeProgressBar translate={translate} barInfo={barInfo} />;
  }

  function showExchangeRate() {
    const robuxAmount = intl.n(devexInfo.minRobuxToCashOut, {
      maximumFractionDigits: 0
    });
    const conversionRate = Number(devexInfo.conversionPercent);
    const amountInUsd = intl.n(devexInfo.minRobuxToCashOut * conversionRate, 'currency', {
      maximumFractionDigits: 0
    });

    const currencyExchangeRateStr = `${translate('Label.CurrentExchangeRate')}* :`;
    const usdAmountStr = translate('Label.RobuxToUSD', {
      robuxAmount,
      usdAmount: `<span class="robux-text">${amountInUsd} USD</span>`
    });

    return (
      <Fragment>
        <div>
          <h3 className='space-above'>{currencyExchangeRateStr}</h3>
          <div dangerouslySetInnerHTML={{ __html: usdAmountStr }} />
          <br />
          <div>
            {translate('Label.CurrentRateCaption', { minimumDevexRobuxAmount: robuxAmount })}
          </div>
        </div>
      </Fragment>
    );
  }

  function showExchangeButton() {
    return (
      devexInfo.canProceedToCashout && (
        <div id='CashoutButton' className='space-above'>
          <a href={getCashoutUrl()} type='button' className='btn-primary btn-medium'>
            {translate('Action.CashOut')}
          </a>
        </div>
      )
    );
  }

  function showOldCashoutOldRateWarning() {
    const warningStr = translate('Description.DevExTermsDisclaimer', {
      helpLinkStart: `<a class="text-link" href='${devexHelpPageUrl}' target='_blank'>`,
      helpLinkEnd: '</a>'
    });
    return (
      <div className='xsmall'>
        <br />
        <span dangerouslySetInnerHTML={{ __html: warningStr }} />
      </div>
    );
  }

  return (
    devexInfo && (
      <div>
        {showTitleMessage()}
        {showSubtitle()}
        {showProgressBar()}
        {showExchangeRate()}
        {showExchangeButton()}
        {showOldCashoutOldRateWarning()}
      </div>
    )
  );
}

DevExWidget.propTypes = {
  translate: PropTypes.func.isRequired,
  intl: PropTypes.func.isRequired
};

export default DevExWidget;
